import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';
import { Download, ClipboardListCheck, AnimatedSpinner } from '@unified-checkout/design-system/icons/index';
import { DataTable } from '@unified-checkout/design-system';
import type {
  IColumnMap,
  IFiltersMap,
} from 'libs/design-system/src/types/data_table';
import type {
  TReportTypeResponse,
  TExportData,
  TReportType,
  TTypeSelectorOptions,
  TReportsHeaderDateRanges,
} from '@app/dashboard/types/innerLayout/reports';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';
import {
  downloadReport,
  getReportsTableData,
  getReportTypes,
} from '@app/dashboard/services/inner_layout/reports';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { downloadReportFile } from '@app/dashboard/utils/downloadReportFile';
import { getReportsTypeCellIcon } from '@app/dashboard/utils/getReportsTypeCellIcon';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { GenerateReport } from '@app/dashboard/components/inner_layout/GenerateReport';
import {
  REPORTS_STATEMENTS_EVENTS,
  REPORTS_STATEMENTS_EVENTS_PARAMS,
  REPORTS_STATEMENTS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/reports-statements';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import styles from './reports-tab.module.scss';

const TABLE_ID = 'reports_table';

export const ReportsTab = ({ headerDateRange }: { headerDateRange: TReportsHeaderDateRanges   }) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;
  const { isLive } = useTestLiveModeStore();
  const { lang } = useLangStore();
  const queryClient = useQueryClient();

  const [isError, setIsError] = useState(false);
  const [downloadingReportData, setDownloadingReportData] = useState<TExportData | null>(null);
  const [filtersMap, setFiltersMap] = useState<IFiltersMap>([] as IFiltersMap);

  const {
    data: reportTypesData,
    isLoading: isReportTypesLoading,
    isError: isReportTypesError,
    error: reportTypesError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_REPORT_TYPES, isLive, lang],
    queryFn: () => getReportTypes({ isLive }).then(res => res.data),
  });

  const {
    data: downloadResponse,
    isLoading: isDownloadingReport,
    error: downloadError,
    isError: isDownloadError,
  } = useQuery({
    queryKey: ['download-report', downloadingReportData?.download_url],
    queryFn: () => downloadReport(downloadingReportData?.download_url!),
    enabled: !!downloadingReportData?.download_url,
  });

  const typeOptions: TTypeSelectorOptions[] = reportTypesData?.results.map((type: TReportTypeResponse) => ({
    label: type.title,
    value: type.api_parameter,
    icon: getReportsTypeCellIcon(type.api_parameter) || ClipboardListCheck,
    id: type.id,
    customized: type.customized,
    hasFilters: type.filters,
    supportsTemplateChoice: type.supports_template_choice,
  })) || [];

  useEffect(() => {
    if (reportTypesData && typeOptions) {
      const formattedTypeOptions: Record<string, string>[] = [];
      let isCustomAdded = false;
      typeOptions.forEach(opt => {
        if (opt.value === 'custom') {
          if (!isCustomAdded) {
            formattedTypeOptions.push({ [t("innerLayout.reportsPage.customReport.title")]: 'custom' });
            isCustomAdded = true;
          }
        } else {
          formattedTypeOptions.push({ [opt.label]: opt.value });
        }
      });

      setFiltersMap([
        {
          key: "report_type",
          type: "select",
          name: t('innerLayout.reportsPage.table.filters.type'),
          values: formattedTypeOptions,
          placeHolder: t('innerLayout.reportsPage.table.filters.type'),
        }
      ]);
    }
  }, [reportTypesData]);

  const columnsMap: IColumnMap[] = [
    {
      id: 'type',
      name: t('innerLayout.reportsPage.table.headers.type'),
      type: 'leading_icon',
      key: 'report_name',
      icon: (value) => getReportsTypeCellIcon(value as TReportType | 'custom')
    },
    {
      id: 'from',
      name: t('innerLayout.reportsPage.table.headers.from'),
      type: 'text',
      key: 'from_time',
    },
    {
      id: 'to',
      name: t('innerLayout.reportsPage.table.headers.to'),
      type: 'text',
      key: 'to_time',
    },
    {
      id: 'created',
      name: t('innerLayout.reportsPage.table.headers.created'),
      type: 'text',
      key: 'created_at',
    },
    {
      id: 'actions',
      name: '',
      type: {
        conditionsType: 'direct_value',
        conditions: {
          action_btns: {
            status: 'Download'
          },
          badge: {
            status: ['Generating', 'Failed ', 'Failed']
          },
        },
        default: 'badge',
      },
      key: {
        conditionsType: 'direct_value',
        conditions: {
          download: {
            status: 'Download'
          },
          pending: {
            status: 'Generating'
          },
          failed: {
            status: ['Failed ', 'Failed']
          }
        },
        default: 'pending',
      },
      label: {
        pending: {
          color: 'yellow',
          text: t('innerLayout.reportsPage.table.status.pending'),
        },
        failed: {
          color: 'red',
          text: t('innerLayout.reportsPage.table.status.failed'),
        }
      },
      actionBtnsProps: (row) => [
        {
          text: t('innerLayout.download'),
          variant: 'outlined',
          leadingIcon: isDownloadingReport && row?.id === downloadingReportData?.id
            ? AnimatedSpinner
            : Download,
          disabled: isDownloadingReport,
          color: 'secondary',
          onClickHandler: (row: TExportData) => onClickDownloadHandler(row)
        },
      ],
    },
  ];

  useEffect(() => {
    if (downloadResponse && downloadingReportData) {
      const downloadResult = downloadReportFile({
        res: downloadResponse,
      });

      if (downloadResult) {
        const reportType = (downloadingReportData as TExportData & { report_name?: string }).report_name ?? downloadingReportData.type;
        const rowWithDates = downloadingReportData as TExportData & { from_time?: string; to_time?: string };
        const fromDate = downloadingReportData.from ?? rowWithDates.from_time ?? '';
        const toDate = downloadingReportData.to ?? rowWithDates.to_time ?? '';
        const dateRange = fromDate && toDate
          ? `${fromDate} - ${toDate}`
          : REPORTS_STATEMENTS_EVENTS_VALUES.DATE_RANGE.EXISTING;
        amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_SUCCEEDED, {
          [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: reportType,
          [REPORTS_STATEMENTS_EVENTS_PARAMS.FORMAT]: downloadResult.format,
          [SHARED_EVENTS_PARAMS.DATE_RANGE]: dateRange,
        });
      }

      setDownloadingReportData(null);
    }
  }, [downloadResponse]);

  useEffect(() => {
    if (isDownloadError && downloadError && downloadingReportData) {
      const errorDescription =
        (typeof downloadError === 'object' &&
          downloadError !== null &&
          'response' in downloadError &&
          (downloadError as any).response?.data?.message) ||
        (downloadError as Error)?.message ||
        t('errors.somethingWentWrong');
      const reportType = (downloadingReportData as TExportData & { report_name?: string }).report_name ?? downloadingReportData.type;
      amplitude.track(REPORTS_STATEMENTS_EVENTS.REPORT_DOWNLOAD_FAILED, {
        [REPORTS_STATEMENTS_EVENTS_PARAMS.REPORT_TYPE]: reportType,
        [REPORTS_STATEMENTS_EVENTS_PARAMS.ERROR_DESCRIPTION]: errorDescription,
      });
      setDownloadingReportData(null);
    }
  }, [isDownloadError, downloadError, downloadingReportData]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_REPORTS, { isLive }],
    });
  }, [filtersMap]);

  const onClickDownloadHandler = (row: TExportData) => {
    if (row.download_url) {
      setDownloadingReportData(row);
    }
  };

    const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_REPORTS, isLive],
    [isLive]
  );
 
  const queryFunctionParams = useMemo(
    () => ({ is_live: isLive, platform: 'dashboard', ...headerDateRange }),
    [isLive, headerDateRange]
  );

  return (
    <div className={styles['reports-tab-wrapper']}>
      <GenerateReport
        typeOptions={typeOptions}
        isReportTypesLoading={isReportTypesLoading}
        isReportTypesError={isReportTypesError}
        reportTypesError={reportTypesError}
        isReportDownloadError={isDownloadError}
        reportDownloadError={downloadError}
        headerDateRange={headerDateRange}
      />

      <DataTable
        lang={lang}
        filters={{
          filtersMap: filtersMap,
          isLoading: isReportTypesLoading,
          isError: isReportTypesError,
          isMobile: isMobile,
          country: getCountryFromDomain(),
        }}
        tableBody={{
          lang: lang,
          tableId: TABLE_ID,
          dataLocation: 'data.results',
          columnsMap: columnsMap,
        }}
        pagination={{
          hideLabels: isMobile,
          type: "cursor_based",
          dataLocation: {
            next: "data.next",
            prev: "data.previous"
          },
          isMobile: isMobile,
        }}
        queries={{
          queryKeys: queryKeys,
          queryFunction: getReportsTableData,
          queryFunctionParams: queryFunctionParams,
        }}
        errorState={{
          onError: (error) => setIsError(error),
        }}
        emptyState={{
          text: t('innerLayout.reportsPage.table.body.noReportsYet'),
          description: t(
            'innerLayout.reportsPage.table.body.noReportsHint'
          ),
        }}
      />
    </div>
  );
};
